/*!

=========================================================
* Argon Dashboard PRO React - v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import { Container, Button, Breadcrumb, BreadcrumbItem } from "reactstrap";
import { useState, useRef, useCallback, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAtomValue, useSetAtom } from "jotai";
import { Whisper, Popover } from "rsuite";
import pluralize from "pluralize";
import _ from "underscore";

// core components
import TenantAPI from "api/api/TenantAPI";
import { generateModuleSettingsTabs } from "utils/GeneralUtils";
import { newToastEvent } from "utils/ToastUtil";
import DropdownOptionWithIcon from "components/Menus/DropdownOptionWithIcon";
import RecordNameTemplate from "components/Records/RecordNameTemplate/RecordNameTemplate";
import AppNavbar from "components/AppNavbar/AppNavbar";
import RecordDetailLayout from "components/Records/RecordDetailLayout";
import DeleteModuleModal from "components/Modals/DeleteModuleModal";
import ManageModuleFields from "components/Modules/ManageModuleFields";
import { modulesAtom } from "atoms/GlobalAtoms";
import useToggle from "hooks/useToggle";
import useUpdateRecordDetailLayout from "api/api-hooks/modules/useUpdateRecordDetailLayout";
import DropdownMenuDivider from "components/Menus/DropdownMenuDivider";
import PopoverDropdownMenu from "components/Menus/PopoverDropdownMenu";
import ManageTags from "components/Tags/ManageTags";
import AppPageHeader from "components/Headers/AppPageHeader";
import { AppConfirm } from "utils/PopupUtil";
import ManageModuleIndexes from "../supportal/CloudDetailView/ModuleIndexes/ManageModuleIndexes";
import useCloudPermissions from "../../hooks/useCloudPermissions";
import AppHelmet from "../../components/AppComponents/AppHelmet";
import ReferencedBy from "./ReferencedBy";
import { setSchemaAtom } from "../../atoms/GlobalAtoms";
import DataSourceSettings from "./DataSourceSettings";
import QuerySettings from "./QuerySettings";
import EditModuleModal from "../../components/Modals/EditModuleModal";

const CRMModuleFieldSettingsManageView = () => {
  const { cPermissions } = useCloudPermissions();
  const navigate = useNavigate();
  const params = useParams();
  const [loading, setLoading] = useState(false);
  const [editNameLoader, setEditNameLoader] = useState(false);
  const [deleteFieldSubmitting, setDeleteFieldSubmitting] = useState("");
  const [activeTab, setActiveTab] = useState("fields");
  const [tabs, setTabs] = useState([]);
  const actionRef = useRef();
  const tableSettingsRef = useRef();
  const tenantID = params?.tenantID;

  const [currentModule, setCurrentModule] = useState(null);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const setSchema = useSetAtom(setSchemaAtom);
  const modules = useAtomValue(modulesAtom);
  const [recordDetailLayoutSections, setRecordDetailLayoutSections] = useState(
    []
  );
  const [
    recordDetailLayoutAdditionalSection,
    setRecordDetailLayoutAdditionalSection,
  ] = useState([]);

  const { updateRecordDetailLayout, updatingRecordDetailLayout } =
    useUpdateRecordDetailLayout();

  // --------- Modal State ---------
  const [deleteModal, toggleDeleteModal] = useToggle();
  const [renameModal, toggleRenameModal] = useToggle();
  const [deleteModuleError, setDeleteModuleError] = useState(null);
  const [renameModuleError, setRenameModuleError] = useState(null);

  const getAllFieldOptions = useCallback(() => {
    let allFields = [];

    if (!currentModule) {
      return allFields;
    }

    allFields = allFields.concat(currentModule.Fields);

    return allFields;
  }, [currentModule]);

  const openEditDetailLayout = useCallback(
    (module) => {
      let out = [];

      const allFields = getAllFieldOptions();

      let visitedFields = [];
      if (module && module.Templates && module.Templates.RecordDetailView) {
        const existingLayouts =
          module.Templates.RecordDetailView.Layouts &&
          module.Templates.RecordDetailView.Layouts.length
            ? module.Templates.RecordDetailView.Layouts
            : [];

        out = existingLayouts;
      }

      let additionalFields = _.filter(allFields, (f) => {
        return visitedFields.indexOf(f.APIName) < 0;
      });

      let additionalFieldsOut = [];

      for (let i = 0; i < additionalFields.length; i++) {
        additionalFieldsOut.push({
          Field: additionalFields[i],
          ColSpan: 1,
        });
      }

      let additionalSection = {
        id: "AdditionalInformation",
        Title: "Additional Information",
        Fields: [],
        Type: "RecordSummarySection",
      };

      setRecordDetailLayoutSections(out);
      setRecordDetailLayoutAdditionalSection(additionalSection);
    },
    [getAllFieldOptions]
  );

  const handleRenameModule = (values) => {
    setEditNameLoader(true);

    TenantAPI.modifyCRMModuleName(currentModule.APIName, values.Name)
      .then(
        (data) => {
          const newSchema = data.data.data.schema;
          handleSchemaUpdate(newSchema);

          toggleRenameModal();
        },
        (err) => {
          console.log(err);

          if (err.response) {
            if (err.response.data && err.response.data.message) {
              setRenameModuleError(err.response.data.message);
              newToastEvent("error", err.response.data.message);
            } else {
              setRenameModuleError(
                "An unknown error occurred. Please try again."
              );
              newToastEvent(
                "error",
                "An unknown error occurred. Please try again."
              );
            }
          }
        }
      )
      .finally(() => setEditNameLoader(false));
  };

  const getModule = useCallback(() => {
    if (!modules) return;
    const matchParams = params;

    const moduleAPIName = matchParams.APIName;
    const currentTab = matchParams.tab;

    if (!moduleAPIName) {
      return navigate(`/clouds/${matchParams?.tenantID}/settings/modules`);
    }

    let currentModule = _.findWhere(modules, { APIName: moduleAPIName });

    if (!currentModule) {
      return navigate(`/clouds/${params?.tenantID}/settings/modules`);
    }

    let tabs = generateModuleSettingsTabs({
      tenantID: params?.tenantID,
      moduleAPIName: currentModule.APIName,
      isTemplate: false,
      templateID: "",
      permissions: cPermissions,
      isDynamic: !!currentModule?.Dynamic,
    });

    setCurrentModule(currentModule);
    setLoading(false);
    setTabs(tabs);
    setActiveTab(currentTab);
    openEditDetailLayout(currentModule);
  }, [navigate, params, modules, openEditDetailLayout]);

  const deleteModuleHandler = () => {
    setDeleteLoader(true);

    TenantAPI.deleteModule(currentModule.APIName)
      .then(
        (data) => {
          const newSchema = data.data.data.schema;

          setSchema(newSchema);

          AppConfirm({
            title: `${currentModule.Name} Successfully Deleted`,
            text: "The module has been successfully deleted.",
            icon: "success",
            confirmButton: "OK",
            confirmButtonColor: "var(--primary)",
            showCancelButton: false,
          });

          navigate(`/clouds/${params?.tenantID}/settings/modules`);
        },
        (err) => {
          console.log(err);

          if (err.response) {
            if (err.response.data && err.response.data.message) {
              setDeleteModuleError(err.response.data.message);
              newToastEvent("error", err.response.data.message);
            } else {
              setDeleteModuleError(
                "An unknown error occurred. Please try again."
              );
              newToastEvent(
                "error",
                "An unknown error occurred. Please try again."
              );
            }
          }
        }
      )
      .finally(() => setDeleteLoader(false));
  };

  const handleSchemaUpdate = (newSchema) => {
    setSchema(newSchema);

    let allModules = newSchema.CRM.Modules;

    let currentMod = _.findWhere(allModules, {
      APIName: currentModule.APIName,
    });

    setCurrentModule(currentMod);
  };

  const handleTabChange = (val) => {
    const newTab = _.findWhere(tabs, { activeValue: val });
    const newLink = newTab.link;

    navigate(newLink, { replace: true });
    setActiveTab(val);
  };

  const handleExportModule = async () => {
    try {
      const data = await TenantAPI.exportModuleJson(currentModule.APIName);
      const donwloadURL = data.data.data;
      window.open(donwloadURL, "_blank");
    } catch (error) {
      console.error(error);
    }
  };

  const TableSettingsOptions = () => {
    return (
      <PopoverDropdownMenu>
        <h5 className="px-2 pt-2 text-dark">Manage</h5>
        <DropdownMenuDivider></DropdownMenuDivider>
        <DropdownOptionWithIcon
          name="Rename Module"
          icon="mdi mdi-pencil-outline"
          onClick={() => {
            actionRef?.current?.close();
            toggleRenameModal();
          }}
          disabled={loading || currentModule?.Protected}
        />
        <DropdownOptionWithIcon
          name="Download Module as Json"
          icon="mdi mdi-download"
          onClick={() => {
            actionRef?.current?.close();
            handleExportModule();
          }}
          disabled={loading}
        />
        <DropdownOptionWithIcon
          name="View All Records"
          icon="mdi mdi-table"
          onClick={() => {
            actionRef?.current?.close();
            navigate(`/clouds/${tenantID}/records/${currentModule?.APIName}`);
          }}
          disabled={loading || (currentModule && currentModule.Protected)}
        />
        <DropdownOptionWithIcon
          name="Delete Module"
          icon="mdi mdi-delete"
          onClick={() => {
            actionRef?.current?.close();
            toggleDeleteModal();
          }}
          disabled={loading || (currentModule && currentModule.Protected)}
          actionColor="danger"
          optionColor="var(--danger)"
        />
      </PopoverDropdownMenu>
    );
  };

  useEffect(() => {
    getModule();
  }, [getModule]);

  return (
    <>
      <AppHelmet
        title={`Settings - Manage ${currentModule?.Name || ""} Data Source`}
      />
      <Container fluid>
        <div className="header">
          <div className="header-body">
            <AppPageHeader
              borderless={true}
              title={currentModule?.Name ?? "Loading..."}
              breadcrumbs={
                <Breadcrumb
                  className="d-block mt-1 mb-1"
                  listClassName="breadcrumb-links"
                >
                  <BreadcrumbItem>
                    <Link to={`/clouds/${tenantID}/settings/home`}>
                      <i className="mdi mdi-cog" />
                    </Link>
                  </BreadcrumbItem>
                  <BreadcrumbItem>
                    <Link to={`/clouds/${tenantID}/settings/modules`}>
                      Data Sources
                    </Link>
                  </BreadcrumbItem>
                  <BreadcrumbItem>{currentModule?.Name}</BreadcrumbItem>
                </Breadcrumb>
              }
              customContent={
                <>
                  <Whisper
                    placement="autoVerticalEnd"
                    trigger="click"
                    ref={tableSettingsRef}
                    speaker={
                      <Popover
                        className="sidebar-popover"
                        full
                        onClick={(e) => {
                          e.stopPropagation();
                          tableSettingsRef.current?.close();
                        }}
                      >
                        <TableSettingsOptions />
                      </Popover>
                    }
                    controlId="newRecordDropdown"
                  >
                    <Button color="secondary" outline className="btn-icon-only">
                      <i className="mdi mdi-dots-horizontal"></i>
                    </Button>
                  </Whisper>
                </>
              }
            />
          </div>
          <div className="mt--4">
            <AppNavbar
              tabs={tabs}
              activeTab={activeTab}
              onSelect={handleTabChange}
            />
          </div>
        </div>
        {activeTab === "fields" && (
          <ManageModuleFields
            moduleAPIName={params.APIName}
            loading={loading}
            deleteFieldSubmitting={deleteFieldSubmitting}
          />
        )}
        {activeTab === "recordnametemplate" && (
          <RecordNameTemplate currentModule={currentModule} />
        )}
        {activeTab === "recordtags" && (
          <div className="mt-3">
            <ManageTags
              title={`${pluralize(currentModule?.Name, 1)} Tags`}
              scope={{ Type: "Module", ModuleAPIName: currentModule?.APIName }}
              description={`Tags created for ${
                currentModule?.APIName
              } can be applied to any ${pluralize(
                currentModule?.APIName,
                1
              )} record.`}
              headerSize="sm"
            />
          </div>
        )}
        {activeTab === "recorddetaillayout" && (
          <RecordDetailLayout
            layouts={recordDetailLayoutSections}
            additionalSection={recordDetailLayoutAdditionalSection}
            module={currentModule}
            onSave={updateRecordDetailLayout}
            submitting={updatingRecordDetailLayout}
          />
        )}
        {activeTab === "indexes" && (
          <ManageModuleIndexes showbreadcrumbs={false} isSupportal={false} />
        )}
        {activeTab === "referencedby" && (
          <ReferencedBy currentModule={currentModule} />
        )}
        {activeTab === "settings" && (
          <DataSourceSettings currentModule={currentModule} />
        )}
        {activeTab === "query" && (
          <QuerySettings currentModule={currentModule} />
        )}
        <DeleteModuleModal
          open={deleteModal}
          toggle={toggleDeleteModal}
          onDelete={deleteModuleHandler}
          moduleName={currentModule?.Name}
          submitting={deleteLoader}
          error={deleteModuleError}
        />
        <EditModuleModal
          open={renameModal}
          toggle={toggleRenameModal}
          onUpdate={handleRenameModule}
          submitting={editNameLoader}
          error={renameModuleError}
          existingName={currentModule?.Name}
        />
      </Container>
    </>
  );
};

export default CRMModuleFieldSettingsManageView;
